'use client'

import { useEffect, useCallback } from 'react'
import { hasKeys, varAlpha } from 'minimal-shared/utils'
import Box from '@mui/material/Box'
import Badge from '@mui/material/Badge'
import Drawer from '@mui/material/Drawer'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { alpha, useColorScheme, useTheme } from '@mui/material/styles'
import { SettingsButton } from '../settings-button'
import { BaseOption } from './base-option'
import { NavColorOptions, NavLayoutOptions } from './nav-layout-options'
import { SmallBlock, LargeBlock } from './styles'
import { PresetsOptions } from './presets-options'
import { FullScreenButton } from './fullscreen-button'
import { FontSizeOptions, FontFamilyOptions } from './font-options'
import { useSettingsContext } from '../context/use-settings-context'
import type { SettingsState, SettingsDrawerProps } from '../types'
import Iconify from '@repo/shared-core-ui-components/Iconify/iconify'
import { primaryColorPresets } from '../../theme/with-settings/color-presets'
import { themeConfig } from '../../theme/theme-config'
import Scrollbar from '@repo/shared-core-ui-components/feedback/scrollbar/Scrollbar'
import { ThemeColorScheme } from '../../theme/types'

// --------------------------------------------------------------------------------------------- //

export function SettingsDrawer({ sx, defaultSettings }: SettingsDrawerProps) {
  const theme = useTheme()
  const settings = useSettingsContext()

  const { mode, setMode, systemMode } = useColorScheme()

  useEffect(() => {
    if (mode === 'system' && systemMode) {
      settings.setState({ colorScheme: systemMode })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, systemMode])

  // Visible options by default settings
  const isFontFamilyVisible = hasKeys(defaultSettings, ['fontFamily'])
  const isCompactLayoutVisible = hasKeys(defaultSettings, ['compactLayout'])
  const isDirectionVisible = hasKeys(defaultSettings, ['direction'])
  const isColorSchemeVisible = hasKeys(defaultSettings, ['colorScheme'])
  const isContrastVisible = hasKeys(defaultSettings, ['contrast'])
  const isNavColorVisible = hasKeys(defaultSettings, ['navColor'])
  const isNavLayoutVisible = hasKeys(defaultSettings, ['navLayout'])
  const isPrimaryColorVisible = hasKeys(defaultSettings, ['primaryColor'])
  const isFontSizeVisible = hasKeys(defaultSettings, ['fontSize'])

  const handleReset = useCallback(() => {
    settings.onReset()
    setMode(defaultSettings.colorScheme as ThemeColorScheme)
  }, [defaultSettings.colorScheme, setMode, settings])

  const renderHead = () => (
    <Box
      sx={{
        py: 2,
        pr: 1,
        pl: 2.5,
        display: 'flex',
        alignItems: 'center'
      }}
    >
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Settings
      </Typography>

      <FullScreenButton />

      <Tooltip title="Reset all">
        <IconButton onClick={handleReset}>
          <Badge color="error" variant="dot" invisible={!settings.canReset}>
            <Iconify icon="solar:restart-bold" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Tooltip title="Close">
        <IconButton onClick={settings.onCloseDrawer}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      </Tooltip>
    </Box>
  )

  const renderMode = () => (
    <BaseOption
      label="Dark mode"
      icon="moon"
      selected={settings.state.colorScheme === 'dark'}
      onChangeOption={() => {
        setMode(mode === 'light' ? 'dark' : 'light')
        settings.setState({ colorScheme: mode === 'light' ? 'dark' : 'light' })
      }}
    />
  )

  const renderContrast = () => (
    <BaseOption
      label="Contrast"
      icon="contrast"
      selected={settings.state.contrast === 'hight'}
      onChangeOption={() =>
        settings.setState({
          contrast: settings.state.contrast === 'default' ? 'hight' : 'default'
        })
      }
    />
  )

  const renderRtl = () => (
    <BaseOption
      label="Right to left"
      icon="align-right"
      selected={settings.state.direction === 'rtl'}
      onChangeOption={() => settings.setState({ direction: settings.state.direction === 'ltr' ? 'rtl' : 'ltr' })}
    />
  )

  const renderCompact = () => (
    <BaseOption
      tooltip="Dashboard only and available at large resolutions > 1600px (xl)"
      label="Compact"
      icon="autofit-width"
      selected={!!settings.state.compactLayout}
      onChangeOption={() => settings.setState({ compactLayout: !settings.state.compactLayout })}
    />
  )

  const renderPresets = () => (
    <LargeBlock
      title="Presets"
      canReset={settings.state.primaryColor !== defaultSettings.primaryColor}
      onReset={() => settings.setState({ primaryColor: defaultSettings.primaryColor })}
    >
      <PresetsOptions
        options={
          Object.keys(primaryColorPresets).map(key => ({
            name: key,
            value: primaryColorPresets[key].main
          })) as { name: SettingsState['primaryColor']; value: string }[]
        }
        value={settings.state.primaryColor}
        onChangeOption={newOption => settings.setState({ primaryColor: newOption })}
      />
    </LargeBlock>
  )

  const renderNav = () => (
    <LargeBlock title="Nav" tooltip="Dashboard only" sx={{ gap: 2.5 }}>
      {isNavLayoutVisible && (
        <SmallBlock
          label="Layout"
          canReset={settings.state.navLayout !== defaultSettings.navLayout}
          onReset={() => settings.setState({ navLayout: defaultSettings.navLayout })}
        >
          <NavLayoutOptions
            options={['vertical', 'horizontal', 'mini']}
            value={settings.state.navLayout}
            onChangeOption={newOption => settings.setState({ navLayout: newOption })}
          />
        </SmallBlock>
      )}
      {isNavColorVisible && (
        <SmallBlock
          label="Color"
          canReset={settings.state.navColor !== defaultSettings.navColor}
          onReset={() => settings.setState({ navColor: defaultSettings.navColor })}
        >
          <NavColorOptions options={['integrate', 'apparent']} value={settings.state.navColor} onChangeOption={newOption => settings.setState({ navColor: newOption })} />
        </SmallBlock>
      )}
    </LargeBlock>
  )

  const fontOptions = [...new Set([themeConfig?.fontFamily?.primary, 'Public Sans Variable', 'Barlow', 'Inter Variable', 'DM Sans Variable', 'Nunito Sans Variable'])]

  const renderFont = () => (
    <LargeBlock title="Font" sx={{ gap: 2.5 }}>
      {isFontFamilyVisible && (
        <SmallBlock
          label="Family"
          canReset={settings.state.fontFamily !== defaultSettings.fontFamily}
          onReset={() => settings.setState({ fontFamily: defaultSettings.fontFamily })}
        >
          <FontFamilyOptions
            // options={[themeConfig.fontFamily.primary, 'Inter Variable', 'DM Sans Variable', 'Nunito Sans Variable']}
            options={fontOptions}
            value={settings.state.fontFamily}
            onChangeOption={newOption => settings.setState({ fontFamily: newOption })}
          />
        </SmallBlock>
      )}
      {isFontSizeVisible && (
        <SmallBlock
          label="Size"
          canReset={settings.state.fontSize !== defaultSettings.fontSize}
          onReset={() => settings.setState({ fontSize: defaultSettings.fontSize })}
          sx={{ gap: 5 }}
        >
          <FontSizeOptions options={[12, 20]} value={settings.state.fontSize} onChangeOption={newOption => settings.setState({ fontSize: newOption })} />
        </SmallBlock>
      )}
    </LargeBlock>
  )

  return (
    <>
      {!settings.openDrawer && (
        <Box
          sx={{
            p: 0.5,
            right: 10,
            bottom: 75,
            zIndex: 999,
            borderRadius: '50%',
            border: `1px solid ${theme.palette.divider}`,
            position: 'fixed',
            boxShadow: `-12px 12px 32px -4px ${alpha(theme.palette.mode === 'light' ? theme.palette.grey[600] : theme.palette.common.black, 0.36)}`,
            ...theme.mixins.bgBlur({ color: theme.palette.background.default })
          }}
        >
          <SettingsButton />
        </Box>
      )}

      <Drawer
        anchor="right"
        open={settings.openDrawer}
        onClose={settings.onCloseDrawer}
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{
          sx: [
            theme => ({
              ...theme.mixins.paperStyles(theme, {
                color: varAlpha(theme.vars.palette.background.defaultChannel, 0.9)
              }),
              width: 360
            }),
            ...(Array.isArray(sx) ? sx : [sx])
          ]
        }}
      >
        {renderHead()}

        <Scrollbar>
          <Box
            sx={{
              pb: 5,
              gap: 6,
              px: 2.5,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box sx={{ gap: 2, display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)' }}>
              {isColorSchemeVisible && renderMode()}
              {isContrastVisible && renderContrast()}
              {isDirectionVisible && renderRtl()}
              {isCompactLayoutVisible && renderCompact()}
            </Box>

            {(isNavColorVisible || isNavLayoutVisible) && renderNav()}
            {isPrimaryColorVisible && renderPresets()}
            {(isFontFamilyVisible || isFontSizeVisible) && renderFont()}
          </Box>
        </Scrollbar>
      </Drawer>
    </>
  )
}
